// Predefined, verifiable prediction markets for community tips.
// Mirrors betMateBackEnd/src/constants/predictionOutcomeCatalog.js — the
// backend validates every selection against the same catalog and grades
// outcomes after full time.

const PREDICTION_MARKETS = {
  oneXTwo: {
    fullTime: ["1", "X", "2"],
    halfTime: ["1", "X", "2"],
    secondHalf: ["1", "X", "2"],
  },
  doubleChance: {
    fullTime: ["1X", "X2", "12"],
    halfTime: ["1X", "X2", "12"],
    secondHalf: ["1X", "X2", "12"],
  },
  bothTeamsToScore: {
    fullTime: ["yes", "no"],
    halfTime: ["yes", "no"],
    secondHalf: ["yes", "no"],
  },
  overUnderGoals: {
    fullTime: [
      "over1_5", "under1_5", "over2_5", "under2_5", "over3_5", "under3_5",
      "over4_5", "under4_5", "over5_5", "under5_5",
    ],
    halfTime: ["over0_5", "under0_5", "over1_5", "under1_5", "over2_5", "under2_5"],
    homeTeam: [
      "over0_5", "under0_5", "over1_5", "under1_5", "over2_5", "under2_5",
      "over3_5", "under3_5",
    ],
    awayTeam: [
      "over0_5", "under0_5", "over1_5", "under1_5", "over2_5", "under2_5",
      "over3_5", "under3_5",
    ],
  },
  correctScore: {
    fullTime: [
      "0-0", "0-1", "0-2", "0-3", "0-4", "0-5", "0-6",
      "1-0", "1-1", "1-2", "1-3", "1-4", "1-5",
      "2-0", "2-1", "2-2", "2-3", "2-4",
      "3-0", "3-1", "3-2", "3-3",
      "4-0", "4-1", "4-2", "5-0", "5-1", "6-0",
    ],
    halfTime: ["0-0", "0-1", "0-2", "1-0", "1-1", "1-2", "2-0", "2-1", "2-2"],
  },
  cleanSheet: {
    homeTeam: ["yes", "no"],
    awayTeam: ["yes", "no"],
  },
  oddEven: {
    fullTime: ["odd", "even"],
    halfTime: ["odd", "even"],
    secondHalf: ["odd", "even"],
  },
  teamToScore: {
    fullTime: ["noGoal", "homeTeam", "awayTeam", "bothTeams"],
  },
  winToNil: {
    homeTeam: ["yes", "no"],
    awayTeam: ["yes", "no"],
  },
  drawNoBet: {
    fullTime: ["homeTeam", "awayTeam"],
    halfTime: ["homeTeam", "awayTeam"],
    secondHalf: ["homeTeam", "awayTeam"],
  },
};

const SECTION_LABELS = {
  oneXTwo: "Match result",
  doubleChance: "Double chance",
  bothTeamsToScore: "Both teams to score",
  overUnderGoals: "Over/Under goals",
  correctScore: "Correct score",
  cleanSheet: "Clean sheet",
  oddEven: "Odd/Even goals",
  teamToScore: "Team to score",
  winToNil: "Win to nil",
  drawNoBet: "Draw no bet",
};

const SUB_SECTION_LABELS = {
  fullTime: "Full time",
  halfTime: "Half time",
  secondHalf: "2nd half",
  homeTeam: "Home team",
  awayTeam: "Away team",
};

const OVER_UNDER_LABELS = {
  over0_5: "Over 0.5", under0_5: "Under 0.5",
  over1_5: "Over 1.5", under1_5: "Under 1.5",
  over2_5: "Over 2.5", under2_5: "Under 2.5",
  over3_5: "Over 3.5", under3_5: "Under 3.5",
  over4_5: "Over 4.5", under4_5: "Under 4.5",
  over5_5: "Over 5.5", under5_5: "Under 5.5",
};

/** Flat list of {id, section, subSection, label} for market group selectors. */
export const marketGroups = Object.entries(PREDICTION_MARKETS).flatMap(
  ([section, subs]) =>
    Object.keys(subs).map((subSection) => ({
      id: `${section}:${subSection}`,
      section,
      subSection,
      label: `${SECTION_LABELS[section]} — ${SUB_SECTION_LABELS[subSection]}`,
    }))
);

export const keysForGroup = (section, subSection) =>
  PREDICTION_MARKETS?.[section]?.[subSection] || [];

/** Short label for a selectable outcome key, with team names when relevant. */
export function keyLabel(section, key, { homeName, awayName } = {}) {
  const home = homeName || "Home";
  const away = awayName || "Away";
  switch (section) {
    case "oneXTwo":
      return key === "1" ? `${home} win` : key === "2" ? `${away} win` : "Draw";
    case "doubleChance":
      return key === "1X" ? `${home} or Draw` : key === "X2" ? `Draw or ${away}` : `${home} or ${away}`;
    case "bothTeamsToScore":
    case "cleanSheet":
    case "winToNil":
      return key === "yes" ? "Yes" : "No";
    case "overUnderGoals":
      return OVER_UNDER_LABELS[key] || key;
    case "correctScore":
      return key;
    case "oddEven":
      return key === "odd" ? "Odd" : "Even";
    case "teamToScore":
      if (key === "noGoal") return "No goal";
      if (key === "homeTeam") return `${home} only`;
      if (key === "awayTeam") return `${away} only`;
      return "Both teams";
    case "drawNoBet":
      return key === "homeTeam" ? home : away;
    default:
      return key;
  }
}

/** Full human label for a pick — market + selection (used in reviews/summaries). */
export function fullOutcomeLabel(market, teamNames = {}) {
  if (!market?.section) return "";
  const group = `${SECTION_LABELS[market.section] || market.section} · ${
    SUB_SECTION_LABELS[market.subSection] || market.subSection
  }`;
  return `${group}: ${keyLabel(market.section, market.key, teamNames)}`;
}

export { PREDICTION_MARKETS, SECTION_LABELS, SUB_SECTION_LABELS };
